import { Component, OnInit } from '@angular/core';
import { LBASuperUserService } from '../../services/lba-super-user.service';
import { ToastService } from 'app/modules/shared/services/toast.service';
import { ResetPasswordService } from 'app/modules/shared/services/reset-password.service';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ReportingService } from 'app/modules/reports/services/reporting.service';
import { IExportColumnInfo } from 'app/modules/reports/interfaces/reporting-data';

@Component({
    selector: 'app-lba-users',
    templateUrl: './lba-users.component.html',
    styleUrls: ['./lba-users.component.scss'],
})
export class LbaUsersComponent implements OnInit {
    lbaUsers: any[];
    lbaUsersDisplay: any[];
    addMenuOpen = false;
    newUserModel = {
        loginEmail: '',
        isSuperAdmin: false,
        isPlatformUser: false,
        isDemoUser: false,
        isActive: true,
        userType: 0,
    };
    userAdded = false;
    userEmail: string;
    userPassword: string;
    errorMessage: string = null;
    showEnabled = true;
    searchTerms: string = '';

    constructor(
        private _superUserService: LBASuperUserService,
        private _resetPasswordService: ResetPasswordService,
        private _toast: ToastService,
        private _reusableFunctions: ReusableFunctions,
        private _reportingService: ReportingService
    ) {}

    ngOnInit() {
        this.getLBAUsers();
    }

    getLBAUsers() {
        this._superUserService.getLBAUsers().subscribe(
            (users) => {
                if (users) {
                    users.forEach((x) => {
                        if (x.isPlatformUser) {
                            x.userType = 1;
                        } else if (x.isSuperUser) {
                            x.userType = 2;
                        } else if (x.isDemoUser) {
                            x.userType = 3;
                        } else {
                            x.userType = 0;
                        }
                    });

                    this.lbaUsers = users;

                    this.filterDisplayByEnabled(this.lbaUsers);
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    newUser() {
        this.addMenuOpen = true;

        this.userAdded = false;
        this.newUserModel = {
            loginEmail: '',
            isSuperAdmin: false,
            isPlatformUser: false,
            isDemoUser: false,
            isActive: true,
            userType: 0,
        };
    }

    saveUser() {
        if (this.newUserModel.loginEmail) {
            if (this.newUserModel.userType == 0) {
                // None
                this.newUserModel.isSuperAdmin = false;
                this.newUserModel.isPlatformUser = false;
                this.newUserModel.isDemoUser = false;
            } else if (this.newUserModel.userType == 1) {
                // Platform User (both flags need to be true)
                this.newUserModel.isSuperAdmin = true;
                this.newUserModel.isPlatformUser = true;
                this.newUserModel.isDemoUser = false;
            } else if (this.newUserModel.userType == 3) {
                this.newUserModel.isSuperAdmin = true;
                this.newUserModel.isPlatformUser = false;
                this.newUserModel.isDemoUser = true;
            } else {
                // Super User
                this.newUserModel.isSuperAdmin = true;
                this.newUserModel.isPlatformUser = false;
                this.newUserModel.isDemoUser = false;
            }

            this._superUserService.addLBAUser(this.newUserModel).subscribe(
                (_) => {
                    this.newUserModel = {
                        loginEmail: '',
                        isSuperAdmin: false,
                        isPlatformUser: false,
                        isDemoUser: false,
                        isActive: true,
                        userType: 0,
                    };
                    this.addMenuOpen = false;
                    this._toast.success('User created.');
                    this.getLBAUsers();
                },
                (error) => {
                    this._toast.warning('Problem creating user.');
                    throw new Error(error);
                }
            );
        }
    }

    resetPassword(user) {
        this._resetPasswordService.requestPasswordReset(user.loginEmail).subscribe(
            () => {
                this._toast.success('Password Reset Email has been sent.');
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    updateUser(user) {
        if (!user.isActive) {
            user.userType = 0;
        }

        if (user.userType == 0) {
            // None
            user.isSuperAdmin = false;
            user.isPlatformUser = false;
            user.isDemoUser = false;
        } else if (user.userType == 1) {
            // Platform User (both flags need to be true)
            user.isSuperAdmin = true;
            user.isDemoUser = false;
            user.isPlatformUser = true;
        } else if (user.userType == 3) {
            user.isSuperAdmin = true;
            user.isDemoUser = true;
            user.isPlatformUser = false;
        } else {
            // Super User
            user.isSuperAdmin = true;
            user.isPlatformUser = false;
            user.isDemoUser = false;
        }

        const params = {
            loginEmail: user.loginEmail,
            isSuperAdmin: user.isSuperAdmin,
            isPlatformUser: user.isPlatformUser,
            isDemoUser: user.isDemoUser,
            isActive: user.isActive,
        };

        this._superUserService.updateUser(params).subscribe(
            (_) => {
                this._toast.success(`Updated ${user.loginEmail}.`);
            },
            (error) => {
                this._toast.warning('Problem saving user.');
                throw new Error(error);
            }
        );
    }

    saveDisabled() {
        if (!this.newUserModel.loginEmail || this.newUserModel.loginEmail.length < 10) {
            return true;
        }

        let validEmail =
            this.newUserModel.loginEmail.includes('@lbaware.com') ||
            this.newUserModel.loginEmail.includes('@simplenexus.com') ||
            this.newUserModel.loginEmail.includes('@ncino.com');
        return !validEmail;
    }

    changeEnabled() {
        this.showEnabled = !this.showEnabled;

        this.emailSearch();
    }

    exportToExcel(requestType: 'house-system-users' | 'clients-power-users') {
        this._toast.info('Export started.');
        this._superUserService.getAllPlatformUsers(requestType).subscribe((res) => {
            let exportName = 'user-export-' + requestType;
            let columnInfo: IExportColumnInfo[] = [];

            if (requestType === 'house-system-users') {
                columnInfo = [
                    {
                        columnId: 1,
                        columnIdName: 'userKey',
                        columnName: 'UserKey',
                        dataType: 'String',
                    },
                    {
                        columnId: 2,
                        columnIdName: 'loginEmail',
                        columnName: 'Login Email',
                        dataType: 'String',
                    },
                    {
                        columnId: 3,
                        columnIdName: 'userType',
                        columnName: 'User Type',
                        dataType: 'String',
                    },
                    {
                        columnId: 4,
                        columnIdName: 'isActive',
                        columnName: 'Active Status',
                        dataType: 'String',
                    },
                    {
                        columnId: 5,
                        columnIdName: 'lastLoginDate',
                        columnName: 'Last Login Date',
                        dataType: 'String',
                    },
                ];
            } else if (requestType === 'clients-power-users') {
                columnInfo = [
                    {
                        columnId: 1,
                        columnIdName: 'clientFullname',
                        columnName: 'Client Full Name',
                        dataType: 'String',
                    },
                    {
                        columnId: 2,
                        columnIdName: 'lfName',
                        columnName: 'Last, First Name',
                        dataType: 'String',
                    },
                    {
                        columnId: 3,
                        columnIdName: 'loginEmail',
                        columnName: 'Login Email',
                        dataType: 'String',
                    },
                    {
                        columnId: 4,
                        columnIdName: 'lastLoginDate',
                        columnName: 'Last Login Date',
                        dataType: 'String',
                    },
                ];
            }

            this._reportingService.setQuickExportData(columnInfo, res, exportName);
        });
    }

    filterDisplayByEnabled(arrayToFilter: any[]) {
        if (this.showEnabled) {
            this.lbaUsersDisplay = arrayToFilter.filter((x) => x.isActive == true);
        } else {
            this.lbaUsersDisplay = arrayToFilter;
        }
    }

    emailSearch() {
        this.filterDisplayByEnabled(
            this.lbaUsers.filter((x) =>
                x.loginEmail.toLowerCase().includes(this.searchTerms.toLowerCase())
            )
        );
    }
}
